import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@shared/api";
import { Banner, Empty, Field, Loading, formatDate } from "@shared/ui";
import {
  PLATFORM_LABELS,
  startOfWeek,
  todayKey,
  addDays,
  formatDayKey,
  type Channel,
  type Plan,
  type TopicSuggestion,
  type Workspace,
} from "@shared/types";

export function PlansPage() {
  const [params, setParams] = useSearchParams();
  const workspaceId = params.get("client") ?? "";

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    api
      .get<{ workspaces: Workspace[] }>("/api/admin/workspaces")
      .then((d) => {
        setWorkspaces(d.workspaces);
        if (!workspaceId && d.workspaces[0]) {
          setParams({ client: d.workspaces[0].id }, { replace: true });
        }
      })
      .catch((e) => setError(e.message));
    // Only on mount: the client is chosen via the URL afterwards.
  }, []);

  const loadPlans = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const data = await api.get<{ plans: Plan[] }>(
        `/api/admin/plans?workspaceId=${workspaceId}`,
      );
      setPlans(data.plans);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load plans");
    }
  }, [workspaceId]);

  useEffect(() => {
    setPlans(null);
    void loadPlans();
  }, [loadPlans]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Content plans</h1>
          <p className="page-sub">
            A plan is one week of posts, drafted together and reviewed as a batch.
          </p>
        </div>
        <button className="primary" onClick={() => setBuilding((v) => !v)} disabled={!workspaceId}>
          {building ? "Cancel" : "New plan"}
        </button>
      </div>

      {error ? <Banner kind="error">{error}</Banner> : null}

      <div className="card" style={{ marginBottom: 16 }}>
        <Field label="Client">
          <select
            value={workspaceId}
            onChange={(e) => setParams({ client: e.target.value })}
            style={{ maxWidth: 360 }}
          >
            {workspaces.length === 0 ? <option value="">No clients yet</option> : null}
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {building && workspaceId ? (
        <PlanBuilder
          workspaceId={workspaceId}
          onDone={() => {
            setBuilding(false);
            void loadPlans();
          }}
        />
      ) : null}

      {!workspaceId ? (
        <Empty title="Add a client first">
          Plans belong to a client. <Link to="/workspaces">Add one</Link> to get started.
        </Empty>
      ) : plans === null ? (
        <Loading label="Loading plans" />
      ) : plans.length === 0 ? (
        <Empty title="No plans yet">
          Create a plan to draft a week of posts in one go.
        </Empty>
      ) : (
        <div className="grid grid-2">
          {plans.map((plan) => (
            <Link
              key={plan.id}
              to={`/plans/${plan.id}`}
              className="card"
              style={{ textDecoration: "none", display: "block" }}
            >
              <div className="card-head">
                <h2>{plan.title}</h2>
                <span className={`pill ${plan.status === "approved" ? "ok" : ""}`}>
                  {plan.status.replace(/_/g, " ")}
                </span>
              </div>
              <p className="muted" style={{ marginTop: 6 }}>
                {plan.postCount ?? 0} post{plan.postCount === 1 ? "" : "s"} · created{" "}
                {formatDate(plan.createdAt)}
              </p>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

function PlanBuilder({ workspaceId, onDone }: { workspaceId: string; onDone: () => void }) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [weekOf, setWeekOf] = useState(() => startOfWeek(addDays(todayKey(), 7)));
  const [topics, setTopics] = useState<string[]>([""]);
  const [direction, setDirection] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState<string[]>([]);

  useEffect(() => {
    api
      .get<{ channels: Channel[] }>(`/api/admin/workspaces/${workspaceId}`)
      .then((d) => {
        setChannels(d.channels);
        setSelected(d.channels.map((c) => c.id));
      })
      .catch((e) => setError(e.message));
  }, [workspaceId]);

  function toggle(id: string) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function suggest() {
    setSuggesting(true);
    setError(null);
    try {
      const res = await api.post<{ topics: TopicSuggestion[] }>(
        "/api/admin/plans/suggest-topics",
        { workspaceId, count: 5, direction: direction || undefined },
      );
      const filled = topics.filter((t) => t.trim());
      setTopics([...filled, ...res.topics.map((t) => t.brief)]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not suggest topics");
    } finally {
      setSuggesting(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFailures([]);
    try {
      const res = await api.post<{ failures?: string[] }>("/api/admin/plans", {
        workspaceId,
        weekOf,
        channelIds: selected,
        topics: topics.map((t) => t.trim()).filter(Boolean),
        direction: direction || undefined,
      });
      if (res.failures?.length) {
        setFailures(res.failures);
      } else {
        onDone();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not build the plan");
    } finally {
      setBusy(false);
    }
  }

  const realTopics = topics.filter((t) => t.trim()).length;

  return (
    <form className="card" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h2 style={{ marginBottom: 14 }}>New plan</h2>
      {error ? <Banner kind="error">{error}</Banner> : null}
      {failures.length ? (
        <Banner kind="warn">
          The plan was created, but {failures.length} topic
          {failures.length === 1 ? "" : "s"} failed to generate. You can add them by hand.
          <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
            {failures.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
          <div className="actions" style={{ marginTop: 10 }}>
            <button type="button" onClick={onDone}>
              Open the plan
            </button>
          </div>
        </Banner>
      ) : null}

      <div className="row">
        <Field label="Week starting" hint={`Monday of the week containing this date.`}>
          <input type="date" value={weekOf} onChange={(e) => setWeekOf(e.target.value)} />
          <p className="field-hint">Posts land on {formatDayKey(startOfWeek(weekOf))} onwards.</p>
        </Field>
      </div>

      <Field label="Channels">
        {channels.length === 0 ? (
          <p className="muted">
            This client has no channels yet. Add one on the client page first.
          </p>
        ) : (
          <div className="actions">
            {channels.map((ch) => (
              <button
                type="button"
                key={ch.id}
                className={selected.includes(ch.id) ? "primary small" : "small"}
                onClick={() => toggle(ch.id)}
              >
                {PLATFORM_LABELS[ch.platform] ?? ch.platform}: {ch.displayName}
              </button>
            ))}
          </div>
        )}
      </Field>

      <Field
        label="Direction"
        hint="Optional steer for the whole week, e.g. focus on the autumn campaign."
      >
        <input value={direction} onChange={(e) => setDirection(e.target.value)} />
      </Field>

      <Field label="Topics" hint="One post per topic.">
        {topics.map((topic, i) => (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              value={topic}
              placeholder={`Topic ${i + 1}`}
              onChange={(e) => {
                const next = [...topics];
                next[i] = e.target.value;
                setTopics(next);
              }}
            />
            <button
              type="button"
              className="small"
              onClick={() => setTopics(topics.filter((_, j) => j !== i))}
              disabled={topics.length === 1}
              aria-label={`Remove topic ${i + 1}`}
            >
              Remove
            </button>
          </div>
        ))}
        <div className="actions">
          <button type="button" className="small" onClick={() => setTopics([...topics, ""])}>
            Add topic
          </button>
          <button type="button" className="small" onClick={() => void suggest()} disabled={suggesting}>
            {suggesting ? "Thinking…" : "Suggest topics"}
          </button>
        </div>
      </Field>

      <div className="actions" style={{ marginTop: 16 }}>
        <button
          type="submit"
          className="primary"
          disabled={busy || realTopics === 0 || selected.length === 0}
        >
          {busy ? `Writing ${realTopics} posts…` : `Draft ${realTopics} post${realTopics === 1 ? "" : "s"}`}
        </button>
        {busy ? <span className="muted">This can take a minute.</span> : null}
      </div>
    </form>
  );
}
